import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserX, Mail, Phone, ShieldCheck } from 'lucide-react';
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function DeleteAccount() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Back to home"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src={LOGO_ICON_BLUE} alt="Rebuilt" className="h-8 w-8" />
          <h1 className="text-xl font-bold text-gray-900">Delete Account</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <UserX className="h-8 w-8 text-red-600" />
            <h2 className="text-3xl font-bold text-gray-900">Delete Your Rebuilt Account</h2>
          </div>

          <p className="text-sm text-gray-500 mb-8">
            <strong>Last Updated:</strong> April 2026
          </p>

          <div className="prose prose-gray max-w-none space-y-8" data-testid="delete-account-content">

            {/* Intro */}
            <section>
              <p className="text-gray-700 leading-relaxed text-lg">
                If you would like to permanently delete your Rebuilt account and associated
                personal information, please email us at{' '}
                <a
                  href="mailto:support@rebuiltindia.co?subject=Account%20Deletion%20Request"
                  className="text-blue-600 font-semibold hover:underline"
                  data-testid="support-email-link"
                >
                  support@rebuiltindia.co
                </a>{' '}
                using your registered email address or mention your registered mobile number.
              </p>
            </section>

            {/* How to Request */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">How to Request Account Deletion</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Please include the following details in your email so we can verify and process your request:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Full name</li>
                <li>Registered mobile number</li>
                <li>Registered email address (if applicable)</li>
              </ul>
            </section>

            {/* Verification */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
                Identity Verification
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We may verify your identity before processing the request. This is to protect your
                account from unauthorised deletion.
              </p>
            </section>

            {/* Timeline */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Processing Timeline</h3>
              <p className="text-gray-700 leading-relaxed">
                Most requests are completed within <strong>7 business days</strong> of verification.
                You will receive a confirmation email once your account has been deleted.
              </p>
            </section>

            {/* Data retention */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Retention Exceptions</h3>
              <p className="text-gray-700 leading-relaxed">
                Some records may be retained where required by applicable law, fraud prevention,
                dispute resolution, security, or regulatory compliance. These records are kept only
                for the minimum period required and are not used for any other purpose.
              </p>
            </section>

            {/* Contact callout */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h3>
              <div className="bg-blue-50 border border-blue-100 p-5 rounded-lg">
                <p className="text-gray-800 flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <strong>Email:</strong>{' '}
                  <a
                    href="mailto:support@rebuiltindia.co"
                    className="text-blue-600 hover:underline"
                  >
                    support@rebuiltindia.co
                  </a>
                </p>
                <p className="text-gray-800 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <strong>Phone:</strong>{' '}
                  <a href="tel:7666020197" className="text-blue-600 hover:underline">
                    7666 020 197
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* CTA buttons */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:support@rebuiltindia.co?subject=Account%20Deletion%20Request&body=Full%20name%3A%20%0ARegistered%20mobile%20number%3A%20%0ARegistered%20email%20address%3A%20%0A%0AI%20would%20like%20to%20permanently%20delete%20my%20Rebuilt%20account."
              className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-center"
              data-testid="cta-email-delete-account"
            >
              Email Deletion Request
            </a>
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
